import { useEffect } from "react";
import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import type { Coords } from "./consts";

/**
 * Highlight given terms in a text by wrapping them in a
 * `<mark className="bg-yellow-200 rounded px-1">` element.
 *
 * @param {string} text - The text to highlight terms in.
 * @param {string[]} terms - The terms to highlight.
 * @returns {React.ReactNode} A React node representing the text with
 *   highlighted terms.
 */
export function highlight(text: string, terms: string[]): React.ReactNode {
  if (!terms.length) return text;

  const pattern = terms
    .filter(Boolean)
    .map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')) // escape regex
    .join('|');

  const regex = new RegExp(`(${pattern})`, 'gi');

  return text.split(regex).map((part, i) =>
    regex.test(part)
      ? <mark key={i} className="bg-yellow-200 rounded px-1">{part}</mark>
      : part
  );
}

/**
 * Debounces the update of a value by a specified delay.
 * 
 * @template T - The type of the input value.
 * @template U - The type of the transformed value.
 * @param {function(U): void} setter - Function to set the transformed value after the debounce delay.
 * @param {T} value - The input value to be transformed and debounced.
 * @param {function(T): U} transformer - Function to transform the input value before setting (defaults to identity function).
 * 
 * This function uses a 300ms delay to debounce the transformation and setting of the value.
 * If the input value changes during the delay, the previous timeout is cleared and a new one is set.
 */

export function useDebouncer<T, U = T>(setter: (value: U) => void, value: T,
  transformer: (value: T) => U = v => v as unknown as U): void {
  useEffect(() => {
    const handler = setTimeout(() => {
      setter(transformer(value));
    }, 300); // 300ms delay

    return () => clearTimeout(handler);
  }, [value, setter, transformer]);
}

export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

export const titleCaseLocation = (location: string) =>
  location.replace(/\p{L}[\p{L}'’]*/gu, word =>
    word.charAt(0).toLocaleUpperCase() + word.slice(1).toLocaleLowerCase()
  );

export const formatDate = (iso?: string): string => {
  if (!iso) return 'N/A';
  const date = new Date(iso);
  const diffDays = Math.ceil(Math.abs(stripTime(new Date()).getTime() - stripTime(date).getTime()) / (1000 * 60 * 60 * 24));

  switch (diffDays) {
    case 0:
      return 'Today';
    case 1:
      return 'Yesterday';
    default:
      return date.toLocaleDateString('en-GB', {
        weekday: 'short',
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      }).replace(',', '');
  }
}


export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export const stripTime = (date: Date): Date => new Date(date.getFullYear(), date.getMonth(), date.getDate());

export function arraysEqual<T>(a: T[] | null | undefined, b: T[] | null | undefined): boolean {
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  return a.every((val, index) => val === b[index]);
};

export function coordsEqual(a: Coords | null | undefined, b: Coords | null | undefined): boolean {
  if (!a || !b) return false;
  return a.lat === b.lat && a.lng === b.lng;
}
